import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Download, BookOpen, Clock, X, User, Building2, Mail, Shield, Brain } from 'lucide-react';
import './Whitepapers.css';

const whitepapers = [
  {
    id: 1,
    title: 'AI vs Human Intelligence',
    author: 'Erasys Ltd',
    category: 'Artificial Intelligence',
    product: 'WhiteMatter',
    description: 'An in-depth exploration of the differences, synergies, and future trajectory of artificial intelligence compared to human cognitive capabilities. Discover how AI and human intelligence can complement each other in the modern workplace.',
    color: '#0071e3',
    icon: Brain,
    readTime: '15 min read',
    pages: 12,
    pdfUrl: 'https://cdn.erasys.co.uk/whitepapers/ERASYS_LTD_WhiteMatter_AI_vs_Human_Intelligence.pdf'
  },
  {
    id: 2,
    title: 'The State of AI Security',
    author: 'Erasys Ltd',
    category: 'Cybersecurity',
    product: 'SafePulse',
    description: 'A comprehensive analysis of the current AI security landscape, examining threats, vulnerabilities, and best practices for professionals. Learn how to protect your organization in an AI-driven world.',
    color: '#5856D6',
    icon: Shield,
    readTime: '20 min read',
    pages: 18,
    pdfUrl: 'https://cdn.erasys.co.uk/whitepapers/Erasys_The_State_of_AI_Security_Professional_v2.pdf'
  }
];

const Whitepapers = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedWhitepaper, setSelectedWhitepaper] = useState(null);
  const [formData, setFormData] = useState({ fullName: '', companyName: '', email: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleDownloadClick = (wp) => {
    setSelectedWhitepaper(wp);
    setFormData({ fullName: '', companyName: '', email: '' });
    setFormErrors({});
    setSubmitSuccess(false);
    setShowModal(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.fullName.trim()) errors.fullName = 'Full name is required';
    if (!formData.companyName.trim()) errors.companyName = 'Company name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await fetch('/api/whitepaper-leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData.fullName,
          companyName: formData.companyName,
          email: formData.email,
          whitepaperTitle: selectedWhitepaper.title,
          whitepaperUrl: selectedWhitepaper.pdfUrl
        })
      });
    } catch (error) {
      console.error('Error saving lead:', error);
    }

    setSubmitting(false);
    setSubmitSuccess(true);

    // Open PDF in new tab
    window.open(selectedWhitepaper.pdfUrl, '_blank');

    setTimeout(() => {
      setShowModal(false);
      setSubmitSuccess(false);
    }, 2000);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="whitepapers-page">
      <section className="whitepapers-hero">
        <div className="whitepapers-hero-content">
          <motion.h1
            className="whitepapers-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Whitepapers
          </motion.h1>
          <motion.p
            className="whitepapers-hero-subtitle"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            In-depth research and analysis from <span className="whitepapers-gradient-text">Erasys</span>
          </motion.p>
        </div>
      </section>

      <section className="whitepapers-grid-section">
        <div className="whitepapers-container">
          <motion.div
            className="whitepapers-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {whitepapers.map((wp) => {
              const IconComponent = wp.icon;
              return (
                <motion.div
                  key={wp.id}
                  className="wp-card"
                  variants={itemVariants}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  <div className="wp-card-inner">
                    <div className="wp-icon-wrapper">
                      <div 
                        className="wp-icon"
                        style={{ backgroundColor: `${wp.color}15` }}
                      >
                        <IconComponent size={32} style={{ color: wp.color }} />
                      </div>
                    </div>

                    <div className="wp-content">
                      <span className="wp-category" style={{ color: wp.color }}>
                        {wp.category}
                      </span>
                      <h3 className="wp-card-title">{wp.title}</h3>
                      <p className="wp-author">By {wp.author}</p>
                      <div className="wp-meta-row">
                        <span className="wp-meta-item">
                          <Clock size={14} />
                          {wp.readTime}
                        </span>
                        <span className="wp-meta-item">
                          <FileText size={14} />
                          {wp.pages} pages
                        </span>
                      </div>
                      <p className="wp-card-description">{wp.description}</p>
                    </div>

                    <div className="wp-footer">
                      <button 
                        className="wp-download-btn"
                        style={{ background: `linear-gradient(135deg, ${wp.color} 0%, ${wp.color}CC 100%)` }}
                        onClick={() => handleDownloadClick(wp)}
                      >
                        <Download size={18} />
                        <span>Download Whitepaper</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Lead Capture Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="wp-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="wp-modal"
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className="wp-modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>

              {submitSuccess ? (
                <div className="wp-modal-success">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <BookOpen size={48} color="#34C759" />
                  </motion.div>
                  <h3>Download Started!</h3>
                  <p>Your whitepaper is opening in a new tab.</p>
                </div>
              ) : (
                <>
                  <div className="wp-modal-header">
                    <div 
                      className="wp-modal-icon"
                      style={{ backgroundColor: `${selectedWhitepaper?.color}15` }}
                    >
                      <FileText size={28} style={{ color: selectedWhitepaper?.color }} />
                    </div>
                    <h3>Download Whitepaper</h3>
                    <p className="wp-modal-wp-title">{selectedWhitepaper?.title}</p>
                    <p className="wp-modal-subtitle">Please fill in your details to download this whitepaper.</p>
                  </div>

                  <form className="wp-modal-form" onSubmit={handleSubmit}>
                    <div className="wp-form-group">
                      <label htmlFor="wp-fullName">
                        <User size={16} />
                        Full Name
                      </label>
                      <input
                        id="wp-fullName"
                        type="text"
                        placeholder="John Doe"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        className={formErrors.fullName ? 'wp-input-error' : ''}
                      />
                      {formErrors.fullName && <span className="wp-error-msg">{formErrors.fullName}</span>}
                    </div>

                    <div className="wp-form-group">
                      <label htmlFor="wp-companyName">
                        <Building2 size={16} />
                        Company Name
                      </label>
                      <input
                        id="wp-companyName"
                        type="text"
                        placeholder="Acme Inc."
                        value={formData.companyName}
                        onChange={(e) => handleInputChange('companyName', e.target.value)}
                        className={formErrors.companyName ? 'wp-input-error' : ''}
                      />
                      {formErrors.companyName && <span className="wp-error-msg">{formErrors.companyName}</span>}
                    </div>

                    <div className="wp-form-group">
                      <label htmlFor="wp-email">
                        <Mail size={16} />
                        Email Address
                      </label>
                      <input
                        id="wp-email"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        className={formErrors.email ? 'wp-input-error' : ''}
                      />
                      {formErrors.email && <span className="wp-error-msg">{formErrors.email}</span>}
                    </div>

                    <button 
                      type="submit" 
                      className="wp-submit-btn"
                      disabled={submitting}
                      style={{ background: `linear-gradient(135deg, ${selectedWhitepaper?.color} 0%, ${selectedWhitepaper?.color}CC 100%)` }}
                    >
                      {submitting ? (
                        <span className="wp-submit-loading">Submitting...</span>
                      ) : (
                        <>
                          <Download size={18} />
                          <span>Download Now</span>
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Whitepapers;
